import { useEffect, useRef, type ReactNode } from 'react'
import { ArrowUp, Mic, Paperclip, Square, X } from 'lucide-react'
import { Spinner } from '@/components/ui/shadcn-io/spinner'
import { formatSeconds, type VoiceRecorder } from './useVoiceRecorder'

/**
 * The chat-style input: a box that grows with what is typed, and a row of
 * controls underneath it: attach on the left, mic and send on the right.
 *
 * It is the same component on every screen. On a phone it is pinned to the
 * bottom like a messaging app, because that is where a thumb already is and
 * where every chat app has taught people to look. On a desktop it sits in
 * the side panel. Either way the agent types, talks or attaches, then sends.
 *
 * The box grows line by line up to a ceiling, then scrolls inside, so a long
 * description never pushes the controls off the screen.
 *
 * While recording, the controls row becomes the recorder: a timer, a cancel
 * and a stop. While generating, it shows what the AI is doing right now.
 */
interface AIComposerProps {
    value: string
    onChange: (next: string) => void
    onSend: () => void
    onAttach: () => void
    canSend: boolean
    busy: boolean
    /** What is happening while busy, e.g. "Reading the title…". */
    progress?: string | null
    recorder: VoiceRecorder
    onRecorded: (file: File) => void
    placeholder: string
    /** Small line above the box: counts, the last notice, an error. */
    hint?: ReactNode
    hintTone?: 'muted' | 'warn' | 'error'
    /** Pinned bar on phones; taller box in the side panel. */
    variant: 'bar' | 'panel'
    /** Held files, shown as chips inside the box above the text. */
    attachments?: ReactNode
}

const HINT: Record<NonNullable<AIComposerProps['hintTone']>, string> = {
    muted: 'text-muted-foreground',
    warn: 'text-amber-600',
    error: 'text-red-600',
}

/** Where the box stops growing and starts scrolling, in pixels. */
const MAX_HEIGHT = { bar: 160, panel: 240 }
const MIN_HEIGHT = 24

export default function AIComposer({ value, onChange, onSend, onAttach, canSend, busy, progress, recorder, onRecorded, placeholder, hint, hintTone = 'muted', variant, attachments }: AIComposerProps) {
    const textarea = useRef<HTMLTextAreaElement>(null)
    const recording = recorder.status === 'recording' || recorder.status === 'starting'

    // Grow with the text up to the ceiling, then scroll, like every chat box.
    useEffect(() => {
        const el = textarea.current
        if (!el) return
        el.style.height = '0px'
        const wanted = Math.max(MIN_HEIGHT, el.scrollHeight)
        el.style.height = `${Math.min(wanted, MAX_HEIGHT[variant])}px`
        el.style.overflowY = wanted > MAX_HEIGHT[variant] ? 'auto' : 'hidden'
    }, [value, variant, placeholder])

    const stopAndKeep = async () => {
        const file = await recorder.stop()
        if (file) onRecorded(file)
    }

    const iconButton =
        'flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-40'

    return (
        <div>
            {(hint || recorder.error) && (
                <p role='status' className={`mb-1.5 px-1 text-xs ${recorder.error ? HINT.error : HINT[hintTone]}`}>
                    {recorder.error ?? hint}
                </p>
            )}
            <div
                className={`rounded-xl border bg-white px-3 pb-2 pt-2 transition-colors ${
                    recording ? 'border-red-300' : 'border-gray-200 focus-within:border-gray-400'
                }`}>
                {attachments && <div className='mb-2 max-h-24 overflow-y-auto'>{attachments}</div>}
                <textarea
                    ref={textarea}
                    aria-label='Describe the property or answer the questions'
                    className='block w-full resize-none bg-transparent text-[13px] leading-5 outline-none placeholder:text-muted-foreground disabled:opacity-60'
                    rows={1}
                    maxLength={12000}
                    value={value}
                    disabled={busy || recording}
                    onChange={(e) => onChange(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey && variant === 'panel' && canSend) {
                            e.preventDefault()
                            onSend()
                        }
                    }}
                    placeholder={recording ? 'Listening…' : placeholder}
                />

                <div className='mt-2 flex items-center gap-1.5'>
                    {recording ? (
                        <>
                            <button type='button' aria-label='Discard recording' onClick={recorder.cancel} className={`${iconButton} text-gray-500 hover:bg-gray-100`}>
                                <X className='h-4 w-4' />
                            </button>
                            <div className='flex min-w-0 flex-1 items-center gap-2 text-[13px]'>
                                <span className='relative flex h-2.5 w-2.5 shrink-0'>
                                    <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75' />
                                    <span className='relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500' />
                                </span>
                                <span className='font-medium tabular-nums'>{formatSeconds(recorder.seconds)}</span>
                                <span className='truncate text-xs text-muted-foreground'>English, Malay or Chinese, or all three.</span>
                            </div>
                            <button type='button' aria-label='Stop and keep the recording' onClick={stopAndKeep} className={`${iconButton} bg-red-500 text-white hover:bg-red-600`}>
                                <Square className='h-3.5 w-3.5 fill-current' />
                            </button>
                        </>
                    ) : (
                        <>
                            <button type='button' aria-label='Attach files' disabled={busy} onClick={onAttach} className={`${iconButton} text-gray-600 hover:bg-gray-100`}>
                                <Paperclip className='h-[18px] w-[18px]' />
                            </button>
                            {busy && progress ? (
                                <p className='flex min-w-0 flex-1 items-center gap-2 px-1 text-xs text-muted-foreground' aria-live='polite'>
                                    <Spinner variant='circle' className='h-3.5 w-3.5 shrink-0' />
                                    <span className='truncate'>{progress}</span>
                                </p>
                            ) : (
                                <span className='flex-1' />
                            )}
                            {recorder.status !== 'unsupported' && (
                                <button
                                    type='button'
                                    aria-label='Record a voice note'
                                    disabled={busy}
                                    onClick={recorder.start}
                                    className={`${iconButton} text-gray-600 hover:bg-gray-100`}>
                                    <Mic className='h-[18px] w-[18px]' />
                                </button>
                            )}
                            <button
                                type='button'
                                aria-label='Generate'
                                disabled={!canSend}
                                onClick={onSend}
                                className={`${iconButton} bg-black text-white hover:bg-gray-800`}>
                                {busy ? <Spinner variant='circle' className='h-4 w-4' /> : <ArrowUp className='h-[18px] w-[18px]' />}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}
